// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Previous from "@stoneage/XDL/Icons/ChevronLeft";
import Next from "@stoneage/XDL/Icons/ChevronRight";
import { placeholderStyle } from "@stoneage/XDL/PreStyle/formField";
import { xBaseTheme } from "@stoneage/XDL/types";
import color from "@stoneage/XDL/Utilities/color";
import DatePickerPresentationPropsDefault from "@insite/mobius/DatePicker/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import React from "react";
import { css } from "styled-components";
import { fontConfig } from "@stoneage/XDL/constants";
import { getProp } from "@insite/mobius/utilities";

const datePickerColors = {
    textColor: { color: "secondary", alpha: 1 },
    navColor: { color: "text.main", alpha: 1 },
    hoverColor: { color: "primary", alpha: 1 },
    bgColor: { color: "common.background", alpha: 1 },
    todayColor: { color: "common.ui1", alpha: 1 },
    todayBg: { color: "transparent", alpha: 1 },
    hoverBorder: { color: "primary", alpha: 1 },
};

const dc = (key: keyof typeof datePickerColors) => color(datePickerColors[key].color, datePickerColors[key].alpha);

// Nav arrows, month/year label, day tiles
const calendarStyles = css`
    .react-calendar {
        max-width: 300px;
        /* Prev / Next arrow buttons */
        .react-calendar__navigation__arrow {
            height: auto;
        }
        .react-calendar__navigation__prev-button,
        .react-calendar__navigation__next-button {
            color: ${dc("navColor")};
            background: transparent;
            background-image: none;

            &:enabled {
                &:hover:not(:disabled) {
                    background: transparent;
                    color: ${dc("hoverColor")};
                }
                &:focus:not(:disabled) {
                    background: transparent;
                }
            }
            &:disabled {
                opacity: 0.33;
                cursor: default;
            }

            svg {
                width: 8px;
                height: 14px;
            }
        }

        /* Month / Year label */
        .react-calendar__navigation__label:enabled {
            &:hover:not(:disabled) {
                background: transparent;
                color: ${dc("hoverColor")};
            }
            &:focus:not(:disabled) {
                background: transparent;
                color: ${dc("textColor")};
                outline: none;
            }
        }

        /* All day tile buttons */
        button {
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
            background-color: ${dc("bgColor")};
            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                color: ${dc("textColor")};
                background-color: transparent;
            }
        }

        /* Selected day */
        .react-calendar__tile--active:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth),
        .react-calendar__tile--hasActive:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth) {
            background-color: ${dc("hoverColor")};
            &:hover {
                color: ${({ theme }) => getContrastColor("primary", theme)};
            }
        }

        /* Today's date */
        .react-calendar__tile--now:not(:hover) {
            color: ${dc("todayColor")} !important;
            background-color: ${dc("todayBg")} !important;
            font-weight: ${fontConfig.weight.regular};
            text-decoration: underline;
            text-underline-offset: 4px;
        }

        .react-calendar__year-view__months__month:enabled:hover:not(:disabled) {
            background-color: transparent;
            color: ${dc("hoverColor")};
        }
    }
`;

// Date/time input field
const inputStyles = css`
    .react-datetime-picker {
        min-height: 34px;
        box-shadow: none;
        color: ${dc("textColor")};

        /* Border darkens on hover */
        &:not(:disabled):hover {
            border-color: ${dc("hoverBorder")};
        }

        /* Padding layout when active */
        &:not(:disabled),
        &:focus-within {
            padding: 0 9px;

            .react-datetime-picker__inputGroup {
                padding: 0 9px 0 0;
            }
            .react-datetime-picker__button {
                padding-right: 0;
                display: flex;
                align-items: center;
            }
        }

        /* Placeholder */
        &__inputGroup input::placeholder {
            ${placeholderStyle}
            font-size: ${fontConfig.regularSingleLine.size - 3}px;
        }

        &__inputGroup__input {
            box-shadow: none;
        }
        &__button {
            padding-right: 0;
        }
    }
`;

// Disabled state
const disabledStyles = css`
    .react-datetime-picker--disabled {
        opacity: 0.33;

        &,
        &:not(:disabled):hover {
            border-color: ${color("common.ui7")};
        }

        /* Lock all interactive children */
        button,
        input,
        span {
            cursor: not-allowed !important;
        }

        /* Dim the calendar icon */
        .react-datetime-picker__button ${IconWrapper}:hover {
            color: ${color("common.ui7")};
        }
    }
`;

// Dropdown positioning
const positionStyles = css`
    .react-datetime-picker__calendar--open,
    .react-datetime-picker__calendar--closed,
    .react-datetime-picker__clock--open,
    .react-datetime-picker__clock--closed {
        position: absolute;
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        ...DatePickerPresentationPropsDefault,

        dateTimePickerProps: {
            calendarProps: {
                nextLabel: React.createElement(Next),
                prevLabel: React.createElement(Previous),
            },
        },

        calendarIconProps: {
            src: "Calendar",
            size: 29,
            color: "common.ui7",
            css: css`
                margin-right: -9px;
                && {
                    padding: 6px;
                }
                &:hover {
                    color: ${color("primary")};
                }
            `,
        },

        clearIconProps: {
            src: "X",
            size: 14,
            color: "secondary",
            css: css`
                && {
                    padding: 0;
                }
                &:hover {
                    color: ${color("danger")};
                }
            `,
        },

        placeholders: {
            dayPlaceholder: "DD",
            monthPlaceholder: "MM",
            yearPlaceholder: "YYYY",
        },

        cssOverrides: {
            datePicker: css`
                line-height: 16px;
                min-width: 162px;

                ${calendarStyles}
                ${inputStyles}
                ${disabledStyles}
                ${positionStyles}
            `,
            formField: css`
                width: 100%;
            `,
        },
    },
} as xBaseTheme["datePicker"];
